import {useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import {useJoin, usePublicBallot, useVote} from '../../shared/api/usePublicBallot';
import type {VotingSystem} from '../../shared/ballotTypes';
import {useEventStream} from '../../shared/live/useEventStream';
import {HttpNotFoundError, HttpRequestFailedError} from '../../shared/utils/httpClient';
import {type BallotPicks, toSelections} from './ballotState';
import {CodeEntry} from './codeEntry';
import {BallotStep} from './components/ballotStep';
import {ClosedScreen} from './components/closedScreen';
import {ConfirmSheet} from './components/confirmSheet';
import {NameStep} from './components/nameStep';
import {NiceTryDialog} from './components/niceTryDialog';
import {ThanksSplash} from './components/thanksSplash';
import {WaitingRoom} from './components/waitingRoom';
import {voterScreen} from './voterScreen';

const SEND_FAILED = 'Could not send your vote. Try again.';

export const VoterContainer = () => {
    const code = (useParams().code ?? '').toUpperCase();
    const navigate = useNavigate();
    const query = usePublicBallot(code);
    const join = useJoin(code);
    const vote = useVote(code);
    const [picks, setPicks] = useState<BallotPicks>({});
    const [confirming, setConfirming] = useState(false);
    const [niceTry, setNiceTry] = useState(false);
    const [celebrate, setCelebrate] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEventStream(`/api/public/ballots/${encodeURIComponent(code)}/events`, () => {
        query.refetch();
    });

    if (query.error instanceof HttpNotFoundError) {
        return <CodeEntry notFound onSubmit={(next) => navigate(`/v/${next}`)} />;
    }

    const ballot = query.data;
    const screen = voterScreen(ballot, {confirming});
    const me = ballot?.me;

    const submit = () => {
        setError(null);
        vote.mutate(
            {selections: toSelections(picks)},
            {
                onSuccess: () => setCelebrate(true),
                onError: (failure) => {
                    setConfirming(false);
                    const status = failure instanceof HttpRequestFailedError ? failure.status : 0;
                    if (status === 422) setNiceTry(true);
                    else if (status === 409) query.refetch();
                    else setError(SEND_FAILED);
                },
            },
        );
    };

    if (!ballot || screen === 'loading') {
        return query.isError ? (
            <CodeEntry notFound={false} onSubmit={(next) => navigate(`/v/${next}`)} />
        ) : (
            <main className="ph-page" aria-busy="true" />
        );
    }

    switch (screen) {
        case 'name':
            return (
                <NameStep
                    code={code}
                    title={ballot.title}
                    joining={join.isPending}
                    failed={join.isError}
                    onJoin={(body) => join.mutate(body)}
                />
            );
        case 'waiting':
            return (
                <WaitingRoom
                    title={ballot.title}
                    name={me?.name ?? ''}
                    photoUrl={me?.photo_url ?? null}
                />
            );
        case 'thanks':
            return (
                <ThanksSplash
                    name={me?.name ?? ''}
                    photoUrl={me?.photo_url ?? null}
                    celebrate={celebrate}
                />
            );
        case 'closed':
            return <ClosedScreen revealed={ballot.status === 'revealed'} />;
        default:
            return (
                <>
                    <BallotStep
                        ballot={ballot}
                        receivedAt={query.dataUpdatedAt}
                        picks={picks}
                        onPicks={setPicks}
                        onSelfVote={() => setNiceTry(true)}
                        onSubmit={() => setConfirming(true)}
                        error={error}
                    />
                    {screen === 'confirm' && (
                        <ConfirmSheet
                            system={ballot.system as VotingSystem}
                            picks={picks}
                            options={ballot.options}
                            sending={vote.isPending}
                            onSubmit={submit}
                            onBack={() => setConfirming(false)}
                        />
                    )}
                    {niceTry && (
                        <NiceTryDialog name={me?.name ?? ''} onClose={() => setNiceTry(false)} />
                    )}
                </>
            );
    }
};
